import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import {
  ClassicEditor,
  Autosave,
  Essentials,
  Paragraph,
  Link,
  BlockQuote,
  Bold,
  Table,
  TableToolbar,
  TableProperties,
  TableColumnResize,
  TableCellProperties,
  FontBackgroundColor,
  FontColor,
  FontFamily,
  FontSize,
  Heading,
  Highlight,
  HorizontalLine,
  Indent,
  IndentBlock,
  Code,
  Italic,
  AutoLink,
  List,
  ImageUtils,
  ImageEditing,
  Strikethrough,
  Style,
  GeneralHtmlSupport,
  Subscript,
  Superscript,
  TableCaption,
  Alignment,
  TodoList,
  Underline,
  PlainTableOutput,
} from "ckeditor5";

import "ckeditor5/ckeditor5.css";

const LICENSE_KEY = "GPL";

const NotarialEditor = React.memo(
  ({
    content = "",
    onChange,
    placeholder = "Start typing...",
    onReady,
    navbarHeight = 80,
  }) => {
    const editorRef = useRef(null);
    const containerRef = useRef(null);
    const [isLayoutReady, setIsLayoutReady] = useState(false);
    const [toolbarStyle, setToolbarStyle] = useState({});
    const [placeholderHeight, setPlaceholderHeight] = useState(0);

    useEffect(() => {
      setIsLayoutReady(true);
      return () => setIsLayoutReady(false);
    }, []);

    useEffect(() => {
      const handleScroll = () => {
        if (!containerRef.current || !editorRef.current) return;

        const container = containerRef.current;
        const containerRect = container.getBoundingClientRect();

        const toolbarElement = container.querySelector(".ck-editor__top");
        if (!toolbarElement) return;

        const toolbarHeight = toolbarElement.offsetHeight;
        const editorMain = container.querySelector(".ck-editor__main");
        const editorMainRect = editorMain?.getBoundingClientRect();

        const shouldFix =
          containerRect.top < navbarHeight &&
          editorMainRect &&
          editorMainRect.bottom > navbarHeight + toolbarHeight + 100;

        if (shouldFix) {
          setToolbarStyle({
            position: "fixed",
            top: `${navbarHeight}px`,
            left: `${containerRect.left}px`,
            width: `${containerRect.width}px`,
            zIndex: 999,
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            borderRadius: "0 0 8px 8px",
          });
          setPlaceholderHeight(toolbarHeight);
        } else {
          setToolbarStyle({});
          setPlaceholderHeight(0);
        }
      };

      // NEW: Determine which element to listen to
      const scrollElement = window;

      // NEW: Use addEventListener with capture phase for container scrolling
      if (scrollElement === window) {
        window.addEventListener("scroll", handleScroll, { passive: true });
        window.addEventListener("resize", handleScroll, { passive: true });
      } else {
        scrollElement.addEventListener("scroll", handleScroll, {
          passive: true,
        });
        window.addEventListener("resize", handleScroll, { passive: true });
      }

      const timer = setTimeout(handleScroll, 100);

      return () => {
        if (scrollElement === window) {
          window.removeEventListener("scroll", handleScroll);
        } else {
          scrollElement.removeEventListener("scroll", handleScroll);
        }
        window.removeEventListener("resize", handleScroll);
        clearTimeout(timer);
      };
    }, [navbarHeight]);

    // Apply styles to toolbar element
    useEffect(() => {
      if (!containerRef.current) return;

      const toolbarElement =
        containerRef.current.querySelector(".ck-editor__top");
      if (!toolbarElement) return;

      if (Object.keys(toolbarStyle).length > 0) {
        Object.assign(toolbarElement.style, toolbarStyle);
      } else {
        // Reset styles
        toolbarElement.style.position = "";
        toolbarElement.style.top = "";
        toolbarElement.style.left = "";
        toolbarElement.style.width = "";
        toolbarElement.style.zIndex = "";
        toolbarElement.style.boxShadow = "";
        toolbarElement.style.borderRadius = "";
      }
    }, [toolbarStyle]);

    const editorConfig = useMemo(() => {
      if (!isLayoutReady) return null;

      return {
        toolbar: {
          items: [
            "undo",
            "redo",
            "|",
            "heading",
            "style",
            "|",
            "fontSize",
            "fontFamily",
            "fontColor",
            "fontBackgroundColor",
            "|",
            "bold",
            "italic",
            "underline",
            "strikethrough",
            "subscript",
            "superscript",
            "|",
            "link",
            "insertTable",
            "blockQuote",
            "horizontalLine",
            "|",
            "alignment",
            "|",
            "bulletedList",
            "numberedList",
            "todoList",
            "outdent",
            "indent",
          ],
          shouldNotGroupWhenFull: true,
        },
        ui: {
          viewportOffset: { top: navbarHeight },
        },
        plugins: [
          Alignment,
          AutoLink,
          Autosave,
          BlockQuote,
          Bold,
          Code,
          Essentials,
          FontBackgroundColor,
          FontColor,
          FontFamily,
          FontSize,
          GeneralHtmlSupport,
          Heading,
          Highlight,
          HorizontalLine,
          ImageEditing,
          ImageUtils,
          Indent,
          IndentBlock,
          Italic,
          Link,
          List,
          Paragraph,
          PlainTableOutput,
          Strikethrough,
          Style,
          Subscript,
          Superscript,
          Table,
          TableCaption,
          TableToolbar,
          TableProperties,
          TableColumnResize,
          TableCellProperties,
          TodoList,
          Underline,
        ],
        fontFamily: { supportAllValues: true },
        fontSize: {
          options: [10, 11, 12, 14, "default", 16, 18, 20, 22],
          supportAllValues: true,
        },
        heading: {
          options: [
            {
              model: "paragraph",
              title: "Paragraph",
              class: "ck-heading_paragraph",
            },
            {
              model: "heading1",
              view: "h1",
              title: "Heading 1",
              class: "ck-heading_heading1",
            },
            {
              model: "heading2",
              view: "h2",
              title: "Heading 2",
              class: "ck-heading_heading2",
            },
            {
              model: "heading3",
              view: "h3",
              title: "Heading 3",
              class: "ck-heading_heading3",
            },
            {
              model: "heading4",
              view: "h4",
              title: "Heading 4",
              class: "ck-heading_heading4",
            },
          ],
        },
        htmlSupport: {
          allow: [
            { name: /^.*$/, styles: true, attributes: true, classes: true },
          ],
        },
        initialData: content,
        licenseKey: LICENSE_KEY,
        link: { addTargetToExternalLinks: true, defaultProtocol: "https://" },
        placeholder: placeholder,
        table: {
          contentToolbar: [
            "tableColumn",
            "tableRow",
            "mergeTableCells",
            "tableProperties",
            "tableCellProperties",
          ],
        },
      };
    }, [isLayoutReady, content, placeholder, navbarHeight]);

    const handleReady = (editor) => {
      editorRef.current = editor;
      if (onReady) onReady(editor);
    };

    const handleEditorChange = (event, editor) => {
      const data = editor.getData();
      if (onChange) onChange(data);
    };

    return (
      <div ref={containerRef} className="notarial-editor-container relative">
        <div className="border border-gray-300 dark:border-gray-600 rounded-lg overflow-visible">
          <style>
            {`
              /* Disable CKEditor's built-in sticky */
              .notarial-editor-container .ck.ck-sticky-panel__content,
              .notarial-editor-container .ck.ck-sticky-panel .ck-sticky-panel__content_sticky {
                position: static !important;
                width: 100% !important;
                margin: 0 !important;
              }

              .notarial-editor-container .ck.ck-editor__top.ck-reset_all {
                background: white;
                border-bottom: 1px solid #e5e7eb;
              }
              
              .dark .notarial-editor-container .ck.ck-editor__top.ck-reset_all {
                background: #1f2937;
                border-bottom: 1px solid #374151;
              }

              /* A4 Paper simulation */
              .notarial-editor-container .ck.ck-editor__main {
                background-color: #e5e7eb;
                padding: 20px;
                display: flex;
                justify-content: center;
                min-height: 400px;
              }

              .notarial-editor-container .ck.ck-editor__main > .ck-editor__editable {
                width: 590px !important;
                max-width: 590px !important;
                min-height: 842px;
                margin: 0 auto;
                padding: 40px 50px !important;
                background: white !important;
                box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
                border: none !important;
                box-sizing: border-box;
              }

              .notarial-editor-container .ck.ck-editor__main > .ck-editor__editable:focus {
                border: none !important;
                outline: 2px solid #3b82f6 !important;
                outline-offset: 2px;
              }

              .dark .notarial-editor-container .ck.ck-editor__main {
                background-color: #122726;
              }

              .dark .notarial-editor-container .ck.ck-editor__main > .ck-editor__editable {
                background: #122726 !important;
                color: #f3f4f6;
                box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
              }

              /* Dropdown z-index fix */
              .ck.ck-balloon-panel,
              .ck.ck-dropdown__panel {
                z-index: 9999 !important;
              }
            `}
          </style>

          {/* Placeholder to prevent content jump */}
          {placeholderHeight > 0 && (
            <div style={{ height: `${placeholderHeight}px` }} />
          )}

          {editorConfig && (
            <CKEditor
              editor={ClassicEditor}
              config={editorConfig}
              onReady={handleReady}
              onChange={handleEditorChange}
            />
          )}
        </div>
      </div>
    );
  }
);

NotarialEditor.displayName = "NotarialEditor";

export default NotarialEditor;
